import { useMemo } from 'react';
import InputRating from '@/trio-ui/InputRating';
import InputText from '@/trio-ui/InputText';
import { englishLevelOptions, availabilityOptions } from '@/utils/dropdownOptions';
import { CandidateNoteSpecialFieldsProps } from './CandidateNoteSpecialFields.container';
import * as Styles from './CandidateNoteSpecialFields.styles';

const CandidateNoteSpecialFieldsComponent = function ({
  name,
  candidate,
  onSaveSpecialFields,
  onChangeSpecialFields,
}: CandidateNoteSpecialFieldsProps): JSX.Element {
  const isTechnicalSkills = name === 'Technical Skills';
  const isCulturalFit = name === 'Cultural Fit';
  const isCompensationAndAvailability = name === 'Compensation & Availability';
  const doesntHaveSpecialFields =
    !isTechnicalSkills && !isCulturalFit && !isCompensationAndAvailability;

  const technicalSkillsFields = useMemo(() => {
    return (
      <Styles.SpecialField>
        <Styles.SpecialFieldLabel>Technical Skills Rating</Styles.SpecialFieldLabel>
        <InputRating
          onChangeHandler={onChangeSpecialFields}
          name="technicalSkillsRating"
          value={candidate?.technicalSkillsRating?.toString()}
        />
      </Styles.SpecialField>
    );
  }, [candidate?.technicalSkillsRating, onChangeSpecialFields]);

  const culturalFitFields = useMemo(() => {
    return (
      <>
        <Styles.SpecialField>
          <Styles.SpecialFieldLabel>Soft Skills Rating</Styles.SpecialFieldLabel>
          <InputRating
            onChangeHandler={onChangeSpecialFields}
            name="softSkillsRating"
            value={candidate.softSkillsRating?.toString()}
          />
        </Styles.SpecialField>
        <Styles.SpecialField>
          <Styles.SpecialFieldLabel>Communication Skills Rating</Styles.SpecialFieldLabel>
          <InputRating
            onChangeHandler={onChangeSpecialFields}
            name="communicationSkillsRating"
            value={candidate.communicationSkillsRating?.toString()}
          />
        </Styles.SpecialField>
        <Styles.SpecialField>
          <Styles.SpecialFieldLabel>English Level</Styles.SpecialFieldLabel>
          <Styles.CustomDropdown
            label=""
            name="englishLevel"
            optionsWithLabel={englishLevelOptions}
            defaultValue={candidate.englishLevel ?? ''}
            onChangeDropDown={onChangeSpecialFields}
          />
        </Styles.SpecialField>
      </>
    );
  }, [
    candidate.communicationSkillsRating,
    candidate.englishLevel,
    candidate.softSkillsRating,
    onChangeSpecialFields,
  ]);

  const compensationAndAvailabilityFields = useMemo(() => {
    return (
      <>
        <Styles.SpecialField expanded>
          <Styles.SpecialFieldLabel smallerMargin>Salary Expectations</Styles.SpecialFieldLabel>
          <Styles.SalaryInput>
            <InputText
              placeholder="Type a value"
              id="salaryExpectation"
              name="salaryExpectation"
              value={candidate.salaryExpectation}
              onBlurHandler={onSaveSpecialFields}
              onChangeHandler={onChangeSpecialFields}
            />
          </Styles.SalaryInput>
        </Styles.SpecialField>
        <Styles.SpecialField expanded>
          <Styles.SpecialFieldLabel smallerMargin>Availability</Styles.SpecialFieldLabel>
          <Styles.CustomDropdown
            name="availability"
            options={availabilityOptions}
            defaultValue={candidate.availability ?? ''}
            onChangeDropDown={onChangeSpecialFields}
          />
        </Styles.SpecialField>
      </>
    );
  }, [candidate.availability, candidate.salaryExpectation, onChangeSpecialFields]);

  if (doesntHaveSpecialFields) {
    return <span />;
  }

  return (
    <Styles.SpecialFieldsArea>
      {isTechnicalSkills && technicalSkillsFields}
      {isCulturalFit && culturalFitFields}
      {isCompensationAndAvailability && compensationAndAvailabilityFields}
    </Styles.SpecialFieldsArea>
  );
};

export default CandidateNoteSpecialFieldsComponent;
